import React, { useEffect, useMemo } from "react";
import { Grid, Stack, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { createUser, updateUser } from "../redux/actions/userActions";
import { getRoles } from "../redux/actions/roleActions";

import { castEnumForDisplay } from "../helpers/enumHelpers";
import { cleanFormData } from "../helpers/formHelpers";

const createUserSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string().required("Role is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const updateUserSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string().required("Role is required"),
  userStatus: Yup.string().required("Status is required"),
  password: Yup.string().min(
    6,
    "Password must be at least 6 characters if provided",
  ),
});

const CreateOrUpdateUserForm = ({
  user = null,
  isEditing = false,
  loading = false,
}) => {
  const dispatch = useDispatch();

  const { roles } = useSelector((state) => state.role);

  useEffect(() => {
    if (!roles || roles.length === 0) dispatch(getRoles());
  }, [dispatch, roles]);

  const roleOptions = useMemo(
    () =>
      roles?.map((r) => ({
        value: r.name,
        label: castEnumForDisplay(r.name),
      })) || [],
    [roles],
  );

  const statusOptions = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
  ];

  const initialValues = {
    firstName: user?.firstName || "",
    middleName: user?.middleName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    role: user?.userRole || user?.role || "",
    userStatus: user?.userStatus || "ACTIVE",
    password: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEditing ? updateUserSchema : createUserSchema}
      onSubmit={(values) => {
        const cleanedValues = cleanFormData(values);
        if (isEditing) {
          if (!values.password) {
            delete cleanedValues.password;
          }
          dispatch(updateUser({ id: user.id, values: cleanedValues }));
        } else {
          dispatch(createUser(cleanedValues));
        }
      }}
      enableReinitialize
    >
      {({ dirty, isValid }) => (
        <Form>
          <Stack spacing={2}>
            <Box>
              <Grid container spacing={2}>
                {/* ROW 1: System Config */}
                <Grid item xs={12} sm={isEditing ? 6 : 12}>
                  <FormSelectField
                    name="role"
                    label="User Role"
                    options={roleOptions}
                  />
                </Grid>
                {isEditing && (
                  <Grid item xs={12} sm={6}>
                    <FormSelectField
                      name="userStatus"
                      label="Account Status"
                      options={statusOptions}
                    />
                  </Grid>
                )}

                {/* ROW 2: Names */}
                <Grid item xs={12} sm={4}>
                  <FormTextField name="firstName" label="First Name" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormTextField
                    name="middleName"
                    label="Middle Name (Optional)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormTextField name="lastName" label="Last Name" />
                </Grid>

                {/* ROW 3: Account Credentials */}
                <Grid item xs={12} sm={isEditing ? 12 : 6}>
                  <FormTextField
                    name="email"
                    label="Email Address"
                    type="email"
                    disabled={isEditing}
                  />
                </Grid>

                {/* Password input goes side by side on Create, full width on Edit */}
                <Grid item xs={12} sm={isEditing ? 12 : 6}>
                  <FormTextField
                    name="password"
                    label={
                      isEditing
                        ? "Change Password (Leave blank to keep current)"
                        : "Password"
                    }
                    type="password"
                  />
                </Grid>
              </Grid>
            </Box>

            <SubmitButton
              title={isEditing ? "Save Changes" : "Register User"}
              loading={loading}
              loadingText={isEditing ? "Saving..." : "Creating..."}
              disabled={!dirty || !isValid}
              startIcon={
                isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
              }
              sx={{ mt: 2 }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default CreateOrUpdateUserForm;
